/**
 * The admin inbox's lines (spec
 * docs/superpowers/specs/2026-10-24-feedback-inbox-icy-photosynthesis-design.md).
 *
 * The server logs every dev command aimed at ANOTHER player or at everyone
 * (`dev_command_log`, filled by triggers on the delivery tables), keeping the
 * command's kind, its target and the payload the dev's RPC built. This turns
 * one such row back into the command as it was typed, e.g.
 * `/give user34 bags 2` or `/message global hello`. Pure: no DOM, no client.
 */

export type LoggedRole = 'lower_dev' | 'higher_dev' | 'admin';

export interface CommandLogRow {
  id: string;
  createdAt: string;
  senderUsername: string;
  senderRole: LoggedRole;
  /** The sender's current leaderboard icon key ('' for none). */
  senderAvatar: string;
  command: string;
  /** A username, or 'global'. */
  target: string;
  payload: Record<string, unknown>;
}

/** The name colours the dev popups use: green, blue, and the admin badge's gold. */
export const ROLE_NAME_COLORS: Record<LoggedRole, string> = {
  lower_dev: '#7ddb7d',
  higher_dev: '#4da6ff',
  admin: '#ffd23f',
};

const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

/** The command as the dev typed it, rebuilt from the logged row. */
export function commandText(row: Pick<CommandLogRow, 'command' | 'target' | 'payload'>): string {
  const p = row.payload ?? {};
  const t = row.target;
  switch (row.command) {
    case 'give':
      return `/give ${t} ${str(p.what)} ${str(p.value)}`.trimEnd();
    case 'message':
      return `/message ${t} ${str(p.text)}`.trimEnd();
    case 'remove': {
      const what = str(p.what);
      if (what === 'clear') return `/remove ${t} clear`;
      const amount = typeof p.amount === 'number' ? p.amount : 1;
      if (what === 'tower') return `/remove ${t} tower ${str(p.value)}`;
      if (what === 'item' || what === 'potion') {
        return `/remove ${t} ${what} ${str(p.value)}${amount > 1 ? ` ${amount}` : ''}`;
      }
      return `/remove ${t} ${what} ${amount}`;
    }
    case 'skip':
    case 'ban':
    case 'op':
      return `/${row.command} ${t}`;
    case 'restock':
      return `/restock ${str(p.what) || 'potionshop'}`;
    default:
      return `/${row.command} ${t}`;
  }
}

/** "Dev23 has done the command /message global hello". */
export function inboxLine(row: CommandLogRow): string {
  return `${row.senderUsername} has done the command ${commandText(row)}`;
}

function isLoggedRole(v: unknown): v is LoggedRole {
  return v === 'lower_dev' || v === 'higher_dev' || v === 'admin';
}

/** Re-validate rows off the wire; anything malformed is dropped. */
export function parseCommandLog(data: unknown): CommandLogRow[] {
  if (!Array.isArray(data)) return [];
  const out: CommandLogRow[] = [];
  for (const r of data) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    if (typeof o.id !== 'string' || typeof o.command !== 'string') continue;
    if (typeof o.sender_username !== 'string' || typeof o.target !== 'string') continue;
    if (!isLoggedRole(o.sender_role)) continue;
    out.push({
      id: o.id,
      createdAt: typeof o.created_at === 'string' ? o.created_at : '',
      senderUsername: o.sender_username,
      senderRole: o.sender_role,
      senderAvatar: typeof o.sender_avatar === 'string' ? o.sender_avatar : '',
      command: o.command,
      target: o.target,
      payload:
        o.payload && typeof o.payload === 'object' ? (o.payload as Record<string, unknown>) : {},
    });
  }
  return out;
}
